<template>
    <div class="marcas-container">
        <Header />

        <div class="dashboard">
            <Sidebar />

            <div class="marcas-content">
                <div class="content-header">
                    <h1 class="title">Gestión de Red Aliada (Retail)</h1>

                    <div class="header-top-controls">
                        <div class="search-container">
                            <div class="search-wrapper">
                                <SearchIcon :size="18" class="search-icon" />
                                <input type="text" v-model="searchQuery" placeholder="Buscar por RIF o Razón Social..."
                                    class="search-input" @input="handleSearch" />
                            </div>
                        </div>

                        <button @click="openAddAliadoModal()" class="btn btn-add">
                            <PlusIcon :size="18" />
                            <span>Nuevo Aliado</span>
                        </button>
                    </div>

                    <div class="filter-controls">
                        <div class="alphabet-filter">
                            <button @click="resetLetterFilter" :class="{ active: !selectedLetter }"
                                class="alphabet-button all-button">
                                Todos
                            </button>
                            <button v-for="letter in alphabet" :key="letter" @click="filterByLetter(letter)"
                                :class="{ active: selectedLetter === letter }" class="alphabet-button">
                                {{ letter }}
                            </button>
                        </div>
                    </div>
                </div>

                <div class="table-container shadow card">
                    <table class="marcas-table">
                        <thead>
                            <tr>
                                <th>RIF</th>
                                <th>Aliado (Razón Social)</th>
                                <th>Sucursales</th>
                                <th>Expediente</th>
                                <th>Estado</th>
                                <th class="actions-column">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="loading">
                                <td colspan="6" class="text-center py-8">
                                    <div class="loading-state">
                                        <Loader2Icon class="spinner-icon text-primary" :size="32" />
                                        <p class="mt-2 text-muted">Cargando aliados...</p>
                                    </div>
                                </td>
                            </tr>
                            <tr v-else-if="filteredAliados.length === 0">
                                <td colspan="6" class="text-center py-4 text-muted">No se encontraron aliados
                                    registrados.</td>
                            </tr>
                            <tr v-for="aliado in paginatedAliados" :key="aliado.aliadoId">
                                <td>
                                    <span class="attribute-badge" style="color: #3b82f6;">
                                        {{ aliado.clienteRif }}
                                    </span>
                                </td>
                                <td style="font-weight: 600;">{{ aliado.clienteNombre }}</td>
                                <td>
                                    <div class="sucur-count-badge" @click="viewAliadoDetails(aliado)">
                                        <StoreIcon :size="14" />
                                        {{ aliado.sucursales ? aliado.sucursales.length : 0 }} sucursales
                                    </div>
                                </td>
                                <td>{{ aliado.numeroExpediente }}</td>
                                <td>
                                    <span class="status-badge" :class="aliado.activo ? 'active' : 'inactive'">
                                        {{ aliado.activo ? 'Activo' : 'Inactivo' }}
                                    </span>
                                </td>
                                <td class="actions-column">
                                    <button @click="toggleAliadoActive(aliado)" class="btn-icon btn-toggle"
                                        :title="aliado.activo ? 'Desactivar' : 'Activar'">
                                        <PowerIcon :size="18" :class="aliado.activo ? 'text-success' : 'text-danger'" />
                                    </button>
                                    <button @click="viewAliadoDetails(aliado)" class="btn-icon btn-detail"
                                        title="Ver Detalles/Sucursales">
                                        <EyeIcon :size="18" />
                                    </button>
                                    <button @click="openEditAliadoModal(aliado)" class="btn-icon btn-edit"
                                        title="Editar">
                                        <EditIcon :size="18" />
                                    </button>
                                    <button @click="confirmDeleteAliado(aliado.aliadoId)" class="btn-icon btn-delete"
                                        title="Eliminar">
                                        <TrashIcon :size="18" />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="pagination-controls">
                    <button @click="prevPage" :disabled="currentPage === 1" class="btn-pagination">
                        <ChevronLeftIcon :size="16" /> Anterior
                    </button>
                    <span>Página <strong>{{ currentPage }}</strong> de <strong>{{ totalPages }}</strong></span>
                    <button @click="nextPage" :disabled="currentPage === totalPages" class="btn-pagination">
                        Siguiente
                        <ChevronRightIcon :size="16" />
                    </button>
                </div>

                <div class="total-count-display">
                    Total Aliados Registrados: {{ totalCount }}
                </div>
            </div>
        </div>

        <!-- Aliado Modal (Add/Edit) -->
        <transition name="modal-fade">
            <div v-if="showAliadoModal" class="modal-overlay" @click.self="closeAliadoModal">
                <div class="modal-window large-modal">
                    <div class="modal-header">
                        <div class="modal-title-group">
                            <div class="icon-bg">
                                <UsersIcon :size="24" class="text-primary" />
                            </div>
                            <h2 class="modal-title">{{ isEditing ? 'Editar Aliado' : 'Nuevo Aliado' }}</h2>
                        </div>
                        <button @click="closeAliadoModal" class="btn-close-modal-premium">
                            <XIcon :size="24" />
                        </button>
                    </div>

                    <div class="modal-body">
                        <!-- Step 1: Search Client (Only for creation) -->
                        <div v-if="!isEditing && currentStep === 1" class="step-container">
                            <div class="modal-section-title">Vincular Cliente al Mayor</div>
                            <div class="form-group premium-input">
                                <label>Buscar Cliente por RIF</label>
                                <div class="search-input-wrapper">
                                    <input type="text" v-model="clientSearchRif" placeholder="Ej: J-12345678-9"
                                        @keypress.enter="searchClientForAlly" class="form-control" />
                                    <button @click="searchClientForAlly" class="btn btn-search"
                                        :disabled="searchingClient">
                                        <SearchIcon v-if="!searchingClient" :size="18" />
                                        <div v-else class="small-spinner"></div>
                                    </button>
                                </div>
                            </div>

                            <div v-if="selectedClient" class="client-preview-card animate-fade-in mt-4">
                                <div class="preview-header">
                                    <CheckCircle2Icon :size="20" class="text-success" />
                                    <span>Cliente Encontrado</span>
                                </div>
                                <div class="preview-body">
                                    <div class="p-item"><strong>Razón Social:</strong> {{
                                        selectedClient.nombreRazonSocial }}</div>
                                    <div class="p-item"><strong>RIF:</strong> {{ selectedClient.documentoNumero }}</div>
                                    <div class="p-item"><strong>Contacto:</strong> {{ selectedClient.telCelular1 }}
                                    </div>
                                </div>
                                <button class="btn btn-next" @click="currentStep = 2">
                                    Continuar registro legal
                                    <ArrowRightIcon :size="18" />
                                </button>
                            </div>
                        </div>

                        <!-- Step 2: Legal Info -->
                        <div v-if="isEditing || currentStep === 2" class="step-container">
                            <div class="modal-section-title">Información de Registro Mercantil</div>
                            <div class="form-grid">
                                <div class="form-group premium-input">
                                    <label>Número Expediente</label>
                                    <input v-model="aliadoForm.numeroExpediente" placeholder="Ej: 223-32131"
                                        class="form-control" />
                                </div>
                                <div class="form-group premium-input">
                                    <label>Número Tomo</label>
                                    <input v-model="aliadoForm.numeroTomo" placeholder="Ej: 47-A"
                                        class="form-control" />
                                </div>
                            </div>
                            <div class="form-group premium-input mt-4">
                                <label>Nombre de la Oficina de Registro</label>
                                <input v-model="aliadoForm.nombreRegistroMercantil"
                                    placeholder="Ej: Registro Mercantil Cuarto" class="form-control" />
                            </div>
                            <div class="form-grid mt-4">
                                <div class="form-group premium-input">
                                    <label>Nro. Registro (Año)</label>
                                    <input v-model="aliadoForm.numeroRegistro" placeholder="Ej: 6 del 2019"
                                        class="form-control" />
                                </div>
                                <div class="form-group premium-input">
                                    <label>Acta de Registro (PDF)</label>
                                    <div class="file-drop-zone" @click="$refs.fileInput.click()">
                                        <FileUpIcon :size="20" v-if="!selectedFile" />
                                        <CheckCircleIcon :size="20" v-else class="text-success" />
                                        <span>{{ selectedFile ? selectedFile.name : 'Vincular documento PDF' }}</span>
                                        <input type="file" ref="fileInput" hidden accept=".pdf"
                                            @change="onFileSelected" />
                                    </div>
                                </div>
                            </div>

                            <div class="modal-footer mt-8">
                                <button type="button" @click="closeAliadoModal"
                                    class="btn-cancel-modal">Cancelar</button>
                                <button type="button" @click="saveAliado" class="btn-save-modal" :disabled="saving">
                                    <span v-if="!saving">{{ isEditing ? 'Actualizar Aliado' : 'Registrar Aliado'
                                    }}</span>
                                    <Loader2Icon v-else class="spinner-icon" :size="18" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Ally Details Modal (Sucursales Management) -->
        <transition name="modal-fade">
            <div v-if="selectedAliado" class="modal-overlay" @click.self="selectedAliado = null">
                <div class="modal-window large-modal x-large">
                    <div class="modal-header header-dark-premium">
                        <div class="modal-title-group flex-between-nav w-full">
                            <div class="ally-hero-header-premium">
                                <div class="icon-bulb-premium">
                                    <StoreIcon :size="24" />
                                </div>
                                <div class="hero-text-content">
                                    <h2 class="modal-title-text text-white">{{ selectedAliado.clienteNombre }}</h2>
                                    <span class="rif-subtitle-text">{{ selectedAliado.clienteRif }}</span>
                                </div>
                            </div>

                            <div class="header-actions-premium">
                                <button v-if="!showSucursalFormInternal" class="btn btn-add-highlight-premium"
                                    @click="openAddSucursalInternal">
                                    <PlusIcon :size="18" />
                                    <span>Nueva Sucursal</span>
                                </button>
                                <button v-else class="btn btn-back-internal-premium"
                                    @click="showSucursalFormInternal = false">
                                    <ChevronLeftIcon :size="18" />
                                    <span>Volver a la Lista</span>
                                </button>
                                <button @click="selectedAliado = null" class="btn-close-dark-premium">
                                    <XIcon :size="24" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="modal-body bg-light-premium">
                        <!-- Mode 1: View/List Sucursales -->
                        <div v-if="!showSucursalFormInternal" class="sucursales-management-view-premium">
                            <div class="view-header-description-premium">
                                <h3>Puntos de Venta Registrados</h3>
                                <p>Gestiona las ubicaciones y responsables de este aliado.</p>
                            </div>

                            <div v-if="!selectedAliado.sucursales || selectedAliado.sucursales.length === 0"
                                class="empty-sucursales-premium">
                                <MapPinOffIcon :size="64" class="text-muted" />
                                <p>No hay puntos de venta registrados para este aliado.</p>
                                <button class="btn btn-primary mt-4" @click="openAddSucursalInternal">
                                    Registrar el primer punto de venta
                                </button>
                            </div>

                            <div v-else class="sucur-grid-modern-premium">
                                <div v-for="suc in selectedAliado.sucursales" :key="suc.sucursalId"
                                    class="sucur-modern-card-premium" :class="{ inactive: !suc.activo }">
                                    <div class="sucur-card-head-premium">
                                        <div class="badge-row-premium">
                                            <span class="type-badge-premium" :class="suc.tipoTienda.toLowerCase()">{{
                                                suc.tipoTienda }}</span>
                                            <span class="nro-id-premium">ID: {{ suc.numeroSucursal }}</span>
                                        </div>
                                        <div class="suc-actions-row-premium">
                                            <button @click="toggleSucursalActive(suc)" class="circle-btn-premium"
                                                :title="suc.activo ? 'Desactivar' : 'Activar'">
                                                <PowerIcon :size="14"
                                                    :class="suc.activo ? 'text-success' : 'text-danger'" />
                                            </button>
                                            <button @click="openEditSucursalInternal(suc)" class="circle-btn-premium"
                                                title="Editar">
                                                <EditIcon :size="14" />
                                            </button>
                                            <button @click="confirmDeleteSucursal(suc.sucursalId)"
                                                class="circle-btn-premium text-danger" title="Eliminar">
                                                <TrashIcon :size="14" />
                                            </button>
                                        </div>
                                    </div>
                                    <div class="sucur-card-body-content-premium">
                                        <h4 class="suc-name-premium">{{ suc.nombreSucursal }}</h4>
                                        <div class="suc-meta-premium">
                                            <div class="meta-item-premium">
                                                <MapPinIcon :size="12" />
                                                <span>{{ suc.direccion }}, {{ suc.ciudad }}</span>
                                            </div>
                                            <div class="meta-item-premium" v-if="suc.email">
                                                <MailIcon :size="12" />
                                                <span>{{ suc.email }}</span>
                                            </div>
                                        </div>

                                        <div class="responsables-avatars-row-premium" v-if="suc.responsables?.length">
                                            <div class="avatars-stack-premium">
                                                <div v-for="res in suc.responsables.slice(0, 3)" :key="res.usuarioId"
                                                    class="avatar-mini-premium" :title="res.nombre">
                                                    {{ res.nombre.charAt(0) }}
                                                </div>
                                                <div v-if="suc.responsables.length > 3" class="avatar-more-premium">
                                                    +{{ suc.responsables.length - 3 }}
                                                </div>
                                            </div>
                                            <span class="avatars-label-premium">Gestión activa</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Mode 2: Create/Edit Form -->
                        <div v-else class="sucursal-form-container-premium">
                            <div class="form-view-header-premium">
                                <h3>{{ isEditingSucursalInternal ? 'Modificar' : 'Nuevo' }} Punto de Venta</h3>
                                <p>Completa los datos operacionales y legales de la sucursal.</p>
                            </div>
                            <SucursalForm :initial-data="editingSucursalDataInternal" :loading="savingSucursalInternal"
                                @save="handleSaveSucursalInternal" @cancel="showSucursalFormInternal = false" />
                        </div>
                    </div>
                </div>
            </div>
        </transition>
        <!-- Select Aliado Modal (Direct POS Creation) -->
        <transition name="modal-fade">
            <div v-if="showSelectAliadoModal" class="modal-overlay" @click.self="showSelectAliadoModal = false">
                <div class="modal-window shadow-2xl">
                    <div class="modal-header">
                        <div class="modal-title-group">
                            <div class="icon-bg bg-blue-premium">
                                <UsersIcon :size="24" class="text-primary" />
                            </div>
                            <div>
                                <h2 class="modal-title">Seleccionar Aliado</h2>
                                <p class="modal-subtitle-premium">Elige la empresa para la nueva Sucursal</p>
                            </div>
                        </div>
                        <button @click="showSelectAliadoModal = false" class="btn-close-modal-premium">
                            <XIcon :size="24" />
                        </button>
                    </div>
                    <div class="modal-body">
                        <div class="search-box-premium mb-6">
                            <SearchIcon :size="20" class="text-slate-400" />
                            <input type="text" v-model="aliadoSearchQuery"
                                placeholder="Buscar por RIF o Nombre de empresa..." class="transparent-input w-full" />
                        </div>

                        <div class="selection-scroll-area custom-scrollbar">
                            <div v-for="a in filteredAliadosForSelection" :key="a.aliadoId"
                                @click="selectAliadoForDirectPOS(a)" class="aliado-selection-card">
                                <div class="aliado-info-mini">
                                    <span class="a-name">{{ a.clienteNombre }}</span>
                                    <span class="a-rif">{{ a.clienteRif }}</span>
                                </div>
                                <ArrowRightIcon :size="20" class="arrow-reveal" />
                            </div>
                            <div v-if="filteredAliadosForSelection.length === 0" class="no-results-dim">
                                <SearchIcon :size="48" class="mb-4 opacity-20" />
                                <p>No encontramos aliados con ese nombre.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import Header from '@/components/HeaderDashboard.vue';
import Sidebar from '@/components/Sidebar.vue';
import SucursalForm from './SucursalForm.vue';
import AliadoService from '@/services/AliadoService';
import Swal from 'sweetalert2';
import {
    PlusIcon, SearchIcon, EditIcon, TrashIcon, PowerIcon, EyeIcon,
    ChevronLeftIcon, ChevronRightIcon, XIcon, StoreIcon, UsersIcon,
    FileUpIcon, CheckCircleIcon, CheckCircle2Icon, ArrowRightIcon,
    Loader2Icon, MapPinIcon, MailIcon, MapPinOffIcon
} from 'lucide-vue-next';

// Utility for toasts
const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer)
        toast.addEventListener('mouseleave', Swal.resumeTimer)
    }
});

// Data
const aliados = ref([]);
const loading = ref(true);
const saving = ref(false);
const savingSucursal = ref(false);
const searchQuery = ref('');
const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const selectedLetter = ref("");
const currentPage = ref(1);
const itemsPerPage = 8;

const showAliadoModal = ref(false);
const isEditing = ref(false);
const currentStep = ref(1);
const searchingClient = ref(false);
const selectedClient = ref(null);
const clientSearchRif = ref('');

const aliadoForm = reactive({
    aliadoId: null,
    clienteId: null,
    numeroExpediente: '',
    numeroTomo: '',
    nombreRegistroMercantil: '',
    numeroRegistro: ''
});
const selectedFile = ref(null);

const selectedAliado = ref(null);
const showSelectAliadoModal = ref(false);
const aliadoSearchQuery = ref('');
const showSucursalFormInternal = ref(false);
const isEditingSucursalInternal = ref(false);
const editingSucursalDataInternal = ref(null);
const savingSucursalInternal = ref(false);

const filteredAliadosForSelection = computed(() => {
    if (!aliadoSearchQuery.value) return aliados.value.slice(0, 5);
    const q = aliadoSearchQuery.value.toLowerCase();
    return aliados.value.filter(a => a.clienteNombre.toLowerCase().includes(q)).slice(0, 7);
});

const openDirectSucursalModal = () => {
    aliadoSearchQuery.value = '';
    showSelectAliadoModal.value = true;
};

const selectAliadoForDirectPOS = (aliado) => {
    selectedAliado.value = aliado;
    showSelectAliadoModal.value = false;
    openAddSucursalInternal();
};

// Mappings & Methods
const fetchAliados = async () => {
    loading.value = true;
    try {
        const res = await AliadoService.getAll(searchQuery.value);
        aliados.value = res?.datos || [];
    } catch (error) {
        console.error(error);
    } finally {
        loading.value = false;
    }
};

const handleSearch = () => {
    currentPage.value = 1;
    fetchAliados();
};

const filterByLetter = (letter) => {
    selectedLetter.value = letter;
    currentPage.value = 1;
};

const resetLetterFilter = () => {
    selectedLetter.value = "";
    currentPage.value = 1;
};

const filteredAliados = computed(() => {
    let list = aliados.value;
    if (selectedLetter.value) {
        list = list.filter(a => a.clienteNombre.toUpperCase().startsWith(selectedLetter.value));
    }
    return list;
});

const paginatedAliados = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage;
    return filteredAliados.value.slice(start, start + itemsPerPage);
});

const totalPages = computed(() => Math.ceil(filteredAliados.value.length / itemsPerPage) || 1);
const totalCount = computed(() => filteredAliados.value.length);

const prevPage = () => { if (currentPage.value > 1) currentPage.value--; };
const nextPage = () => { if (currentPage.value < totalPages.value) currentPage.value++; };

// Ally Actions
const openAddAliadoModal = () => {
    isEditing.value = false;
    currentStep.value = 1;
    selectedClient.value = null;
    clientSearchRif.value = '';
    Object.assign(aliadoForm, { aliadoId: null, clienteId: null, numeroExpediente: '', numeroTomo: '', nombreRegistroMercantil: '', numeroRegistro: '' });
    selectedFile.value = null;
    showAliadoModal.value = true;
};

const openEditAliadoModal = (aliado) => {
    isEditing.value = true;
    Object.assign(aliadoForm, {
        aliadoId: aliado.aliadoId,
        clienteId: aliado.clienteId,
        numeroExpediente: aliado.numeroExpediente,
        numeroTomo: aliado.numeroTomo,
        nombreRegistroMercantil: aliado.nombreRegistroMercantil,
        numeroRegistro: aliado.numeroRegistro
    });
    selectedFile.value = null; // Don't reset if we want to keep, but typically we upload new one
    showAliadoModal.value = true;
};

const closeAliadoModal = () => {
    showAliadoModal.value = false;
};

const searchClientForAlly = async () => {
    if (!clientSearchRif.value) return;

    searchingClient.value = true;

    try {
        const res = await AliadoService.buscarClienteByRif(clientSearchRif.value);

        const { datos, exito, errores } = res;

        if (!exito) {
            Swal.fire('Error', errores || 'No se encontró el cliente.', 'error');
            return;
        }

        // cliente encontrado
        selectedClient.value = datos;
        aliadoForm.clienteId = datos.clienteId;

        console.log("Cliente encontrado:", datos);

    } catch (error) {
        console.error(error);
        Swal.fire('Error', 'Error consultando el cliente.', 'error');
    } finally {
        searchingClient.value = false;
    }
};
const onFileSelected = (e) => {
    const file = e.target.files[0];
    if (file) selectedFile.value = file;
};

const saveAliado = async () => {
    saving.value = true;
    try {
        if (isEditing.value) {
            await AliadoService.update(aliadoForm.aliadoId, aliadoForm, selectedFile.value);
            Toast.fire({ icon: 'success', title: 'Aliado actualizado correctamente' });
        } else {
            await AliadoService.register(aliadoForm, selectedFile.value);
            Toast.fire({ icon: 'success', title: 'Aliado registrado correctamente' });
        }
        closeAliadoModal();
        fetchAliados();
    } catch (error) {
        Swal.fire('Error', error.message || 'Error al procesar la solicitud', 'error');
    } finally {
        saving.value = false;
    }
};

const toggleAliadoActive = async (aliado) => {
    const result = await Swal.fire({
        title: '¿Cambiar estado del aliado?',
        text: `¿Estás seguro de que deseas ${aliado.activo ? 'desactivar' : 'activar'} al aliado "${aliado.clienteNombre}"?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, cambiar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#3b82f6',
        cancelButtonColor: '#94a3b8',
    });

    if (!result.isConfirmed) return;

    try {
        await AliadoService.toggleActivo(aliado.aliadoId);
        aliado.activo = !aliado.activo;
        Toast.fire({ icon: 'success', title: `Aliado ${aliado.activo ? 'activado' : 'desactivado'}` });
    } catch (error) {
        Swal.fire('Error', 'No se pudo cambiar el estado', 'error');
    }
};

const confirmDeleteAliado = async (id) => {
    const result = await Swal.fire({
        title: '¿Estás seguro?',
        text: "El aliado se marcará como inactivo permanentemente.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
        try {
            await AliadoService.delete(id);
            Toast.fire({ icon: 'success', title: 'El aliado ha sido eliminado' });
            fetchAliados();
        } catch (error) {
            Swal.fire('Error', 'No se pudo eliminar el aliado.', 'error');
        }
    }
};

const viewAliadoDetails = (aliado) => {
    selectedAliado.value = aliado;
    showSucursalFormInternal.value = false; // Always start with list
};

// Sucursal Actions (Internal)
const openAddSucursalInternal = () => {
    isEditingSucursalInternal.value = false;
    editingSucursalDataInternal.value = null;
    showSucursalFormInternal.value = true;
};

const openEditSucursalInternal = (sucursal) => {
    isEditingSucursalInternal.value = true;
    editingSucursalDataInternal.value = sucursal;
    showSucursalFormInternal.value = true;
};

const handleSaveSucursalInternal = async (data) => {
    savingSucursalInternal.value = true;
    try {
        const { file, ...dto } = data;
        if (isEditingSucursalInternal.value) {
            await AliadoService.updateSucursal(dto.sucursalId, dto, file);
            Toast.fire({ icon: 'success', title: 'Sucursal actualizada' });
        } else {
            await AliadoService.addSucursal(selectedAliado.value.aliadoId, dto, file);
            Toast.fire({ icon: 'success', title: 'Sucursal agregada' });
        }
        showSucursalFormInternal.value = false;

        // Refresh detail view
        const res = await AliadoService.getById(selectedAliado.value.aliadoId);
        selectedAliado.value = res.datos || res;
        fetchAliados();
    } catch (error) {
        Swal.fire('Error', 'Error al guardar sucursal', 'error');
    } finally {
        savingSucursalInternal.value = false;
    }
};

const toggleSucursalActive = async (sucursal) => {
    const result = await Swal.fire({
        title: '¿Cambiar estado de la sucursal?',
        text: `¿Estás seguro de que deseas ${sucursal.activo ? 'desactivar' : 'activar'} la sucursal "${sucursal.nombreSucursal}"?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, cambiar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#3b82f6',
        cancelButtonColor: '#94a3b8',
    });

    if (!result.isConfirmed) return;

    try {
        await AliadoService.toggleSucursalActivo(sucursal.sucursalId);
        sucursal.activo = !sucursal.activo;
        Toast.fire({ icon: 'success', title: `Sucursal ${sucursal.activo ? 'activada' : 'desactivada'}` });
    } catch (error) {
        Swal.fire('Error', 'No se pudo cambiar el estado', 'error');
    }
};

const confirmDeleteSucursal = async (id) => {
    const result = await Swal.fire({
        title: '¿Eliminar Sucursal?',
        text: "Esta acción no se puede deshacer.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
        try {
            await AliadoService.deleteSucursal(id);
            const res = await AliadoService.getById(selectedAliado.value.aliadoId);
            selectedAliado.value = res;
            fetchAliados();
        } catch (error) {
            Swal.fire('Error', 'Error al eliminar', 'error');
        }
    }
};

onMounted(fetchAliados);
</script>

<style scoped>
/* Estilos Base (Copiados de Proveedores para uniformidad) */
.marcas-container {
    min-height: 100vh;
    background-color: #f8fafc;
    font-family: 'Inter', sans-serif;
}

.dashboard {
    display: flex;
}

.marcas-content {
    flex: 1;
    padding: 2.5rem;
    overflow-y: auto;
}

.content-header {
    margin-bottom: 2.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.title {
    font-size: 2rem;
    font-weight: 800;
    color: #0f172a;
    margin: 0;
    letter-spacing: -0.025em;
}

.header-top-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.search-container {
    flex: 1;
    max-width: 500px;
}

.search-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.search-icon {
    position: absolute;
    left: 1rem;
    color: #94a3b8;
}

.search-input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 3rem;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    background: white;
    font-size: 0.95rem;
    transition: all 0.2s;
}

.search-input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
}

.btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.25rem;
    border-radius: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
}

.btn-add {
    background: #2563eb;
    color: white;
    box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2);
}

.btn-add:hover {
    background: #1d4ed8;
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(37, 99, 235, 0.3);
}

.btn-add-secondary {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.25rem;
    border-radius: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: 1px solid #e2e8f0;
    background: white;
    color: #1e293b;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.btn-add-secondary:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    transform: translateY(-2px);
}

.alphabet-filter {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    padding: 10px;
    background: white;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.alphabet-button {
    padding: 6px 10px;
    border: 1px solid transparent;
    background: transparent;
    color: #64748b;
    font-weight: 600;
    border-radius: 6px;
    cursor: pointer;
    transition: 0.2s;
    font-size: 0.85rem;
}

.alphabet-button:hover {
    background: #f1f5f9;
    color: #3b82f6;
}

.alphabet-button.active {
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;
}

.all-button {
    font-weight: 700;
    margin-right: 5px;
}

/* Premium Table */
.table-container {
    background: white;
    border-radius: 20px;
    overflow: hidden;
    margin-bottom: 1.5rem;
    border: 1px solid #e2e8f0;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
}

.marcas-table {
    width: 100%;
    border-collapse: collapse;
}

.marcas-table th {
    background: #f8fafc;
    padding: 1rem 1.5rem;
    text-align: left;
    font-size: 0.85rem;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid #e2e8f0;
}

.marcas-table td {
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
    font-size: 0.95rem;
}

.attribute-badge {
    padding: 0.25rem 0.5rem;
    background: #eff6ff;
    border-radius: 6px;
    font-weight: 700;
    font-size: 0.8rem;
}

.status-badge {
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 700;
}

.status-badge.active {
    background: #dcfce7;
    color: #166534;
}

.status-badge.inactive {
    background: #fee2e2;
    color: #991b1b;
}

.actions-column {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
}

.btn-icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-toggle {
    background: #f8fafc;
    color: #64748b;
}

.btn-toggle:hover {
    background: #f1f5f9;
}

.btn-detail {
    background: #f3e8ff;
    color: #9333ea;
}

.btn-detail:hover {
    background: #d8b4fe;
}

.btn-edit {
    background: #eff6ff;
    color: #3b82f6;
}

.btn-edit:hover {
    background: #dbeafe;
}

.btn-delete {
    background: #fef2f2;
    color: #ef4444;
}

.btn-delete:hover {
    background: #fee2e2;
}

.pagination-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1.5rem;
    margin-top: 2rem;
}

.btn-pagination {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    background: white;
    font-weight: 600;
    cursor: pointer;
}

.btn-pagination:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.total-count-display {
    text-align: center;
    margin-top: 1rem;
    color: #94a3b8;
    font-size: 0.85rem;
}

/* Modal and Step Styling (Matches Premium Design) */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(4px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-window {
    background: white;
    border-radius: 24px;
    width: 90%;
    max-width: 600px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.large-modal {
    max-width: 1100px;
    width: 95%;
}

.x-large {
    max-width: 1400px;
    width: 98%;
}

.modal-header {
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #f1f5f9;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    /* Bases for absolute positioning of the X */
}

.modal-title-group {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.icon-bg {
    width: 48px;
    height: 48px;
    background: #eff6ff;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.modal-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
}

.modal-body {
    padding: 2rem;
    max-height: 80vh;
    overflow-y: auto;
}

.modal-section-title {
    font-size: 0.85rem;
    font-weight: 700;
    color: #3b82f6;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 1.25rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #eff6ff;
}

.premium-input label {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 0.4rem;
}

.form-control {
    width: 100%;
    padding: 0.75rem 1rem;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    transition: all 0.2s;
}

.form-control:focus {
    outline: none;
    border-color: #3b82f6;
    background: white;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
}

.form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
}

.search-input-wrapper {
    display: flex;
    gap: 0.5rem;
}

.btn-search {
    background: #1e293b;
    color: white;
    border-radius: 12px;
    padding: 0 1.25rem;
    border: none;
    cursor: pointer;
}

.client-preview-card {
    background: #f0f9ff;
    border: 1px solid #bae6fd;
    border-radius: 16px;
    padding: 1.5rem;
}

.preview-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #0369a1;
    font-weight: 700;
    margin-bottom: 1rem;
}

.preview-body .p-item {
    margin-bottom: 0.5rem;
    font-size: 0.95rem;
}

.btn-next {
    width: 100%;
    margin-top: 1.5rem;
    background: #0369a1;
    color: white;
    border: none;
    border-radius: 12px;
    padding: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-weight: 700;
}

.file-drop-zone {
    border: 2px dashed #e2e8f0;
    border-radius: 12px;
    padding: 1rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    cursor: pointer;
    color: #64748b;
    font-size: 0.9rem;
}

.file-drop-zone:hover {
    border-color: #3b82f6;
    background: #eff6ff;
    color: #2563eb;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 1.5rem;
    margin-top: 2rem;
}

.mt-4 { margin-top: 1rem; }
.mt-8 { margin-top: 2rem; }

.btn-cancel-modal {
    padding: 0.75rem 1.5rem;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: white;
    font-weight: 600;
    color: #64748b;
    cursor: pointer;
}

.btn-save-modal {
    padding: 0.75rem 1.5rem;
    background: #2563eb;
    color: white;
    border-radius: 12px;
    border: none;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

/* Details Sidebar Panel */
.details-sidebar-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(15, 23, 42, 0.4);
    backdrop-filter: blur(4px);
    z-index: 900;
}

.details-panel {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 100%;
    max-width: 500px;
    background: white;
    box-shadow: -10px 0 50px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    animation: slideInRight 0.3s ease-out;
}

@keyframes slideInRight {
    from {
        transform: translateX(100%);
    }

    to {
        transform: translateX(0);
    }
}

.details-header-premium {
    padding: 2.5rem 2rem;
    background: #0f172a;
    color: white;
}

.details-panel-nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2.5rem;
}

.btn-back-premium {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    color: #e2e8f0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    padding: 0.5rem 1rem;
    border-radius: 10px;
    font-weight: 600;
    font-size: 0.85rem;
    transition: all 0.2s;
}

.btn-back-premium:hover {
    background: rgba(255, 255, 255, 0.2);
    color: white;
}

.btn-add-highlight {
    background: #3b82f6;
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    border: none;
    color: white;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    cursor: pointer;
    padding: 0.65rem 1.25rem;
    border-radius: 12px;
    font-weight: 700;
    font-size: 0.9rem;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
    transition: all 0.2s;
}

.btn-add-highlight:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(37, 99, 235, 0.4);
}

.ally-hero-premium {
    display: flex;
    align-items: center;
    gap: 1.5rem;
}

.hero-icon-container {
    width: 64px;
    height: 64px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%);
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #3b82f6;
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.hero-info h2 {
    font-size: 1.5rem;
    font-weight: 800;
    margin-bottom: 0.5rem;
    letter-spacing: -0.02em;
}

.hero-badges {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.rif-badge {
    background: rgba(59, 130, 246, 0.2);
    color: #60a5fa;
    padding: 0.25rem 0.6rem;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 700;
    border: 1px solid rgba(59, 130, 246, 0.2);
}

.count-pill {
    background: rgba(255, 255, 255, 0.05);
    color: #94a3b8;
    padding: 0.25rem 0.6rem;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 600;
}

.details-body {
    flex: 1;
    overflow-y: auto;
    padding: 2rem;
    background: #f8fafc;
}

.panel-section-title {
    font-size: 0.75rem;
    font-weight: 800;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-bottom: 1.5rem;
}

.btn-add-small {
    font-size: 0.85rem;
    padding: 0.6rem 1rem;
    background: #2563eb;
    color: white;
    border-radius: 10px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.2s;
}

.btn-add-small:hover {
    background: #1d4ed8;
    transform: translateY(-1px);
}

.sucur-cards-list {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.sucur-premium-card {
    background: white;
    border-radius: 24px;
    border: 1px solid #e2e8f0;
    padding: 1.75rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
}

.sucur-premium-card:hover {
    border-color: #3b82f6;
    transform: translateY(-4px);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.sucur-premium-card.inactive {
    opacity: 0.6;
    filter: grayscale(40%);
}

.sucur-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.25rem;
}

.type-badge-group {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.type-tag {
    font-size: 0.65rem;
    font-weight: 800;
    padding: 0.3rem 0.75rem;
    border-radius: 20px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.nro-badge {
    font-size: 0.7rem;
    font-weight: 700;
    color: #94a3b8;
    background: #f8fafc;
    padding: 0.2rem 0.6rem;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
}

.type-tag.express {
    background: #fef2f2;
    color: #ef4444;
}

.type-tag.plus {
    background: #f0fdf4;
    color: #22c55e;
}

.type-tag.mega {
    background: #fffbeb;
    color: #f59e0b;
}

.sucur-actions {
    display: flex;
    gap: 0.4rem;
}

.btn-mini {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f1f5f9;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    color: #64748b;
    transition: all 0.2s;
}

.btn-mini:hover {
    background: #e2e8f0;
    color: #0f172a;
}

.sucur-card-body h4 {
    font-size: 1.25rem;
    font-weight: 800;
    margin-bottom: 1.25rem;
    color: #0f172a;
    letter-spacing: -0.02em;
}

.sucur-info-grid {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.sucur-info-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 0.9rem;
    color: #64748b;
    margin-bottom: 0.5rem;
}

.res-title {
    font-size: 0.75rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.75rem;
    margin-top: 1.25rem;
}

.res-avatars {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
}

.res-item-chip {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.35rem 0.75rem 0.35rem 0.35rem;
    background: #f1f5f9;
    border-radius: 100px;
}

.res-initial {
    width: 24px;
    height: 24px;
    background: #3b82f6;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    font-weight: 800;
}

.res-name-small {
    font-size: 0.8rem;
    font-weight: 600;
    color: #334155;
}

.form-container-inline {
    background: white;
    border-radius: 20px;
    border: 1px solid #e2e8f0;
    padding: 1.5rem;
    animation: fadeIn 0.3s ease-out;
}

.form-header-inline {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #f1f5f9;
}

.form-header-inline h3 {
    font-size: 1.125rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
}

.btn-close-inline {
    background: #f1f5f9;
    border: none;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #64748b;
}

.details-section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid #e2e8f0;
}

.title-with-badge {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.count-badge {
    background: #3b82f6;
    color: white;
    font-size: 0.75rem;
    font-weight: 800;
    padding: 0.2rem 0.6rem;
    border-radius: 20px;
}

.btn-add-premium-small {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border: none;
    padding: 0.6rem 1.25rem;
    border-radius: 12px;
    font-weight: 700;
    font-size: 0.85rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2);
    transition: all 0.2s;
}

.btn-add-premium-small:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(37, 99, 235, 0.3);
}

/* Integrated Sucursal Modal Styles */
.header-dark-premium {
    background: #0f172a;
    padding: 1.5rem 2rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.flex-between-nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.ally-hero-header-premium {
    display: flex;
    align-items: center;
    gap: 1.25rem;
}

.icon-bulb-premium {
    width: 48px;
    height: 48px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #3b82f6;
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-title-text {
    font-size: 1.25rem;
    font-weight: 800;
    margin: 0;
    letter-spacing: -0.01em;
}

.rif-subtitle-text {
    font-size: 0.85rem;
    color: #94a3b8;
    font-weight: 600;
}

.header-actions-premium {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.btn-add-highlight-premium {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border: none;
    padding: 0.65rem 1.25rem;
    border-radius: 12px;
    font-weight: 700;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
    transition: all 0.2s;
}

.btn-add-highlight-premium:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(37, 99, 235, 0.4);
}

.btn-back-internal-premium {
    background: rgba(255, 255, 255, 0.1);
    color: #e2e8f0;
    border: none;
    padding: 0.65rem 1.25rem;
    border-radius: 12px;
    font-weight: 700;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-back-internal-premium:hover {
    background: rgba(255, 255, 255, 0.2);
    color: white;
}

.btn-close-dark-premium {
    position: absolute;
    top: 1.5rem;
    right: 1.5rem;
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.6);
    width: 36px;
    height: 36px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    z-index: 10;
}

.btn-close-dark-premium:hover {
    color: #f87171;
    transform: rotate(90deg);
}

.btn-close-modal-premium {
    position: absolute;
    top: 1.5rem;
    right: 1.5rem;
    background: transparent;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    z-index: 10;
}

.btn-close-modal-premium:hover {
    color: #ef4444;
    transform: rotate(90deg);
}

.bg-light-premium {
    background: #f8fafc;
    min-height: 500px;
}

.view-header-description-premium {
    margin-bottom: 2rem;
}

.view-header-description-premium h3 {
    font-size: 1.125rem;
    font-weight: 800;
    color: #0f172a;
    margin-bottom: 0.25rem;
}

.view-header-description-premium p {
    color: #64748b;
    font-size: 0.9rem;
}

.sucur-grid-modern-premium {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 1.5rem;
}

.sucur-modern-card-premium {
    background: white;
    border-radius: 20px;
    border: 1px solid #e2e8f0;
    padding: 1.5rem;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
}

.sucur-modern-card-premium.inactive {
    background: #f8fafc;
    border-color: #e2e8f0;
    opacity: 0.7;
    filter: grayscale(0.8);
    pointer-events: auto;
    /* Allow toggling it back on */
}

.sucur-modern-card-premium.inactive:hover {
    filter: grayscale(0.2);
    opacity: 0.9;
}

.sucur-modern-card-premium:hover {
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05);
    border-color: #3b82f6;
    transform: translateY(-2px);
}

.sucur-card-head-premium {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.badge-row-premium {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.type-badge-premium {
    font-size: 0.65rem;
    font-weight: 800;
    padding: 0.25rem 0.6rem;
    border-radius: 8px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.type-badge-premium.express {
    background: #fef2f2;
    color: #ef4444;
}

.type-badge-premium.plus {
    background: #f0fdf4;
    color: #22c55e;
}

.type-badge-premium.mega {
    background: #fffbeb;
    color: #f59e0b;
}

.nro-id-premium {
    font-size: 0.7rem;
    color: #94a3b8;
    font-weight: 700;
}

.suc-actions-row-premium {
    display: flex;
    gap: 0.25rem;
}

.circle-btn-premium {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: none;
    background: #f1f5f9;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
}

.circle-btn-premium:hover {
    background: #e2e8f0;
    color: #0f172a;
}

.suc-name-premium {
    font-size: 1.1rem;
    font-weight: 800;
    color: #0f172a;
    margin-bottom: 0.75rem;
}

.suc-meta-premium {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.meta-item-premium {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.85rem;
    color: #64748b;
}

.responsables-avatars-row-premium {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #f1f5f9;
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.avatars-stack-premium {
    display: flex;
    align-items: center;
}

.avatar-mini-premium {
    width: 24px;
    height: 24px;
    background: #3b82f6;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.65rem;
    font-weight: 800;
    border: 2px solid white;
    margin-left: -8px;
}

.avatar-mini-premium:first-child {
    margin-left: 0;
}

.avatar-more-premium {
    width: 24px;
    height: 24px;
    background: #f1f5f9;
    color: #64748b;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.6rem;
    font-weight: 700;
    border: 2px solid white;
    margin-left: -8px;
}

.avatars-label-premium {
    font-size: 0.75rem;
    font-weight: 600;
    color: #94a3b8;
}

/* Form transition inside modal */
.sucursal-form-container-premium {
    animation: slideUpFade 0.4s ease-out;
}

@keyframes slideUpFade {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.form-view-header-premium {
    margin-bottom: 2rem;
}

.empty-sucursales-premium {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 5rem 2rem;
    text-align: center;
}

.empty-sucursales {
    text-align: center;
    padding: 4rem 2rem;
    color: #94a3b8;
}

.empty-icon {
    margin-bottom: 1.5rem;
    color: #cbd5e1;
}

.sucur-count-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 0.75rem;
    background: #f1f5f9;
    border-radius: 10px;
    font-size: 0.85rem;
    font-weight: 600;
    color: #475569;
    cursor: pointer;
    transition: all 0.2s;
}

.sucur-count-badge:hover {
    background: #e2e8f0;
    color: #0f172a;
}

.spinner-icon {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

.subtitle {
    font-size: 0.85rem;
    color: #64748b;
    display: block;
    margin-top: 0.2rem;
}

/* --- PREMIUM SELECTION MODAL STYLES --- */
.bg-blue-premium {
    background: #eff6ff !important;
}

.modal-subtitle-premium {
    font-size: 0.85rem;
    color: #64748b;
    margin: 0;
    font-weight: 500;
}

.search-box-premium {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    padding: 0.85rem 1.25rem;
    border-radius: 16px;
    transition: all 0.3s ease;
}

.search-box-premium:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
    background: white;
}

.transparent-input {
    border: none;
    background: transparent;
    flex: 1;
    font-size: 1rem;
    outline: none;
    color: #1e293b;
    font-weight: 500;
}

.selection-scroll-area {
    max-height: 400px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding-right: 0.5rem;
}

.aliado-selection-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 18px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.aliado-selection-card:hover {
    border-color: #3b82f6;
    background: #f0f7ff;
    transform: translateX(6px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.08);
}

.aliado-info-mini {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.a-name {
    font-weight: 800;
    color: #0f172a;
    font-size: 1rem;
}

.a-rif {
    font-size: 0.8rem;
    color: #3b82f6;
    font-weight: 700;
}

.arrow-reveal {
    color: #94a3b8;
    opacity: 0;
    transform: translateX(-10px);
    transition: all 0.3s ease;
}

.aliado-selection-card:hover .arrow-reveal {
    opacity: 1;
    transform: translateX(0);
    color: #3b82f6;
}

.no-results-dim {
    padding: 4rem 2rem;
    text-align: center;
    color: #94a3b8;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.custom-scrollbar::-webkit-scrollbar {
    width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #cbd5e1;
}
</style>
